import type {
  AssistantChartResponse,
  AssistantMemoryResponse,
  AssistantStopReasonEnum,
  AssistantTableResponse,
} from 'src/api/ProactApiClient';
import {handleUnauthorized} from 'src/shared/utils/AxiosInterceptors';
import {getAccessToken, getApiBaseUrl} from 'src/shared/utils/apiConfig';

export type AssistantMemoryAction = 'saved' | 'forgotten';

// Typed mirror of the backend AssistantStreamEvent contract
// (Proact.Shared/Services/Assistant/Models/AssistantStreamEvent.cs).
export type AssistantStreamEvent =
  | {type: 'text'; text: string}
  | {type: 'tool_started'; callId: string; tool: string}
  | {type: 'tool_finished'; callId: string; tool: string; isError: boolean}
  | {type: 'chart'; chart: AssistantChartResponse}
  | {type: 'table'; table: AssistantTableResponse}
  | {type: 'memory'; note: AssistantMemoryResponse; action: AssistantMemoryAction}
  | {type: 'error'; code: string; message: string}
  | {
      type: 'done';
      messageId: number;
      truncated: boolean;
      stopReason: AssistantStopReasonEnum | undefined;
      usedMemoryIds: number[];
    };

export interface StreamAssistantMessageOptions {
  conversationId: number;
  content: string;
  thinkHarder?: boolean;
  signal?: AbortSignal;
  onEvent: (event: AssistantStreamEvent) => void;
}

const isChartPayload = (value: unknown): value is AssistantChartResponse => {
  if (typeof value !== 'object' || value === null) return false;
  const chart = value as Partial<AssistantChartResponse>;
  return Array.isArray(chart.series) && Array.isArray(chart.points);
};

const isTablePayload = (value: unknown): value is AssistantTableResponse => {
  if (typeof value !== 'object' || value === null) return false;
  const table = value as Partial<AssistantTableResponse>;
  return Array.isArray(table.columns) && Array.isArray(table.rows);
};

const isMemoryPayload = (value: unknown): value is AssistantMemoryResponse => {
  if (typeof value !== 'object' || value === null) return false;
  const note = value as Partial<AssistantMemoryResponse>;
  return typeof note.id === 'number' && typeof note.text === 'string';
};

const isMemoryAction = (value: unknown): value is AssistantMemoryAction =>
  value === 'saved' || value === 'forgotten';

const toStopReason = (value: unknown): AssistantStopReasonEnum | undefined =>
  typeof value === 'number' ? (value as AssistantStopReasonEnum) : undefined;

const toIdList = (value: unknown): number[] =>
  Array.isArray(value)
    ? value.filter((id): id is number => typeof id === 'number')
    : [];

const parseEvent = (
  eventName: string,
  data: string
): AssistantStreamEvent | undefined => {
  let payload: Record<string, unknown> = {};
  if (data) {
    try {
      payload = JSON.parse(data) as Record<string, unknown>;
    } catch {
      return undefined;
    }
  }

  switch (eventName) {
    case 'text':
      return {type: 'text', text: String(payload.text ?? '')};
    case 'tool_started':
      return {
        type: 'tool_started',
        callId: String(payload.callId ?? ''),
        tool: String(payload.tool ?? ''),
      };
    case 'tool_finished':
      return {
        type: 'tool_finished',
        callId: String(payload.callId ?? ''),
        tool: String(payload.tool ?? ''),
        isError: Boolean(payload.isError),
      };
    case 'memory':
      return isMemoryPayload(payload.note) && isMemoryAction(payload.action)
        ? {type: 'memory', note: payload.note, action: payload.action}
        : undefined;
    case 'chart':
      return isChartPayload(payload.chart)
        ? {type: 'chart', chart: payload.chart}
        : undefined;
    case 'table':
      return isTablePayload(payload.table)
        ? {type: 'table', table: payload.table}
        : undefined;
    case 'error':
      return {
        type: 'error',
        code: String(payload.code ?? 'error'),
        message: String(payload.message ?? 'The assistant hit an error.'),
      };
    case 'done':
      return {
        type: 'done',
        messageId: Number(payload.messageId ?? 0),
        truncated: Boolean(payload.truncated),
        stopReason: toStopReason(payload.stopReason),
        usedMemoryIds: toIdList(payload.usedMemoryIds),
      };
    default:
      return undefined;
  }
};

// Comment lines (`:` heartbeats) and unknown events are ignored.
const dispatchFrame = (
  frame: string,
  onEvent: (event: AssistantStreamEvent) => void
) => {
  let eventName = 'message';
  const dataLines: string[] = [];

  for (const rawLine of frame.split('\n')) {
    const line = rawLine.replace(/\r$/, '');
    if (!line || line.startsWith(':')) continue;

    if (line.startsWith('event:')) {
      eventName = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).replace(/^ /, ''));
    }
  }

  const parsed = parseEvent(eventName, dataLines.join('\n'));
  if (parsed) onEvent(parsed);
};

export const streamAssistantMessage = async ({
  conversationId,
  content,
  thinkHarder = false,
  signal,
  onEvent,
}: StreamAssistantMessageOptions): Promise<void> => {
  const baseUrl = getApiBaseUrl();
  const accessToken = await getAccessToken();

  const response = await fetch(
    `${baseUrl}/assistant/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(accessToken ? {Authorization: `Bearer ${accessToken}`} : {}),
      },
      body: JSON.stringify({content, thinkHarder}),
      signal,
    }
  );

  if (response.status === 401) {
    handleUnauthorized();
    return;
  }

  if (!response.ok || !response.body) {
    onEvent({
      type: 'error',
      code: `http_${response.status}`,
      message:
        response.status === 409
          ? 'This conversation already has a message in progress.'
          : 'The assistant is unavailable right now. Please try again.',
    });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const {done, value} = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, {stream: true});

    let separatorIndex = buffer.indexOf('\n\n');
    while (separatorIndex !== -1) {
      const frame = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + 2);
      dispatchFrame(frame, onEvent);
      separatorIndex = buffer.indexOf('\n\n');
    }
  }

  if (buffer.trim()) dispatchFrame(buffer, onEvent);
};
